import React, { useState, useEffect } from 'react';
import { toolsAPI, suppliersAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';

const methods = ['Internal Calibration', 'External Agency', 'OEM'];

export default function AddToolDialog({ open, onOpenChange, onCreated, editTool }) {
  const isEdit = !!editTool;
  const [approvedSuppliers, setApprovedSuppliers] = useState([]);
  const [eligibleItems, setEligibleItems] = useState([]);
  const [form, setForm] = useState({
    instrument_name: editTool?.instrument_name || '',
    serial_number: editTool?.serial_number || '',
    instrument_code: editTool?.instrument_code || '',
    inventory_item_id: editTool?.inventory_item_id || '',
    certification_no: editTool?.certification_no || '',
    range_size: editTool?.range_size || '',
    least_count: editTool?.least_count || '',
    frequency_days: editTool?.frequency_days || 365,
    calibration_date: editTool?.calibration_date?.substring(0, 10) || '',
    location: editTool?.location || '',
    method: editTool?.method || '',
    acceptance_criteria: editTool?.acceptance_criteria || '',
    standard_used_for_criteria: editTool?.standard_used_for_criteria || '',
    preferred_calibration_agency: editTool?.preferred_calibration_agency || '',
    remark: editTool?.remark || '',
  });
  const [loading, setLoading] = useState(false);

  // Load approved suppliers + eligible Item Master items when dialog opens
  useEffect(() => {
    if (open) {
      loadApprovedSuppliers();
      if (!isEdit) loadEligibleItems();
    }
  }, [open]);

  const loadApprovedSuppliers = async () => {
    try {
      const res = await suppliersAPI.approvedList();
      setApprovedSuppliers(res.data);
    } catch (err) {
      console.error('Failed to load suppliers');
    }
  };

  const loadEligibleItems = async () => {
    try {
      const res = await toolsAPI.eligibleItems();
      setEligibleItems(res.data || []);
    } catch (err) {
      console.error('Failed to load eligible items');
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectItem = (itemId) => {
    const it = eligibleItems.find(i => i.id === itemId);
    setForm(prev => ({
      ...prev,
      inventory_item_id: itemId,
      instrument_name: it ? it.item_name : '',
      instrument_code: it ? (it.external_id || it.unique_id || '') : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !form.inventory_item_id) {
      toast.error('Please select an instrument from the Item Master');
      return;
    }
    if (!form.serial_number) {
      toast.error('Serial Number is required');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        frequency_days: parseInt(form.frequency_days) || 365,
        calibration_date: form.calibration_date ? new Date(form.calibration_date).toISOString() : null,
      };
      if (isEdit) {
        await toolsAPI.update(editTool.id, payload);
        toast.success('Instrument updated');
      } else {
        await toolsAPI.create(payload);
        toast.success('Instrument created');
      }
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save instrument');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-tool-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-[#0F172A]">
            {isEdit ? 'Edit Instrument' : 'Add Instrument'}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {isEdit ? 'Update instrument details' : 'Register an instrument from the Item Master (Fixed Asset with Calibration Required)'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Item Master selection (create only) */}
          {!isEdit && (
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Select Instrument from Item Master *</Label>
              <Select value={form.inventory_item_id || undefined} onValueChange={handleSelectItem}>
                <SelectTrigger className="h-8 text-sm" data-testid="tool-item-select">
                  <SelectValue placeholder="Choose a calibration-required Fixed Asset..." />
                </SelectTrigger>
                <SelectContent>
                  {eligibleItems.map(it => (
                    <SelectItem key={it.id} value={it.id}>
                      {it.item_name} {it.unique_id ? `(${it.unique_id})` : ''}{it.stock != null ? ` — stock: ${it.stock}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {eligibleItems.length === 0 && (
                <p className="text-[10px] text-amber-600">
                  No eligible items. Add a <b>Fixed Asset</b> with <b>Calibration Required</b> and <b>non-zero stock</b> in the Item Master first, then register it here.
                </p>
              )}
            </div>
          )}

          {/* Row 1: Name & Code (read-only — sourced from Item Master) */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Instrument Name</Label>
              <Input
                value={form.instrument_name}
                readOnly
                placeholder="Auto-filled from Item Master"
                className="h-8 text-sm bg-slate-50 cursor-not-allowed"
                data-testid="tool-name-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Instrument Code</Label>
              <Input
                value={form.instrument_code}
                readOnly
                placeholder="Auto-filled from Item Master"
                className="h-8 text-sm font-mono bg-slate-50 cursor-not-allowed"
                data-testid="tool-code-input"
              />
            </div>
          </div>

          {/* Row 2: Serial & Cert */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Serial Number *</Label>
              <Input
                value={form.serial_number}
                onChange={(e) => handleChange('serial_number', e.target.value)}
                placeholder="e.g. SN-2024-001"
                className="h-8 text-sm font-mono"
                data-testid="tool-serial-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Certification No.</Label>
              <Input
                value={form.certification_no}
                onChange={(e) => handleChange('certification_no', e.target.value)}
                placeholder="Latest certificate number"
                className="h-8 text-sm font-mono"
                data-testid="tool-cert-no-input"
              />
            </div>
          </div>

          {/* Row 3: Range & Least Count */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Range / Size</Label>
              <Input
                value={form.range_size}
                onChange={(e) => handleChange('range_size', e.target.value)}
                placeholder="e.g. 0-150mm"
                className="h-8 text-sm"
                data-testid="tool-range-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Least Count</Label>
              <Input
                value={form.least_count}
                onChange={(e) => handleChange('least_count', e.target.value)}
                placeholder="e.g. 0.01mm"
                className="h-8 text-sm"
                data-testid="tool-least-count-input"
              />
            </div>
          </div>

          {/* Row 4: Frequency & Cal Date */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Frequency (days)</Label>
              <Input
                type="number"
                value={form.frequency_days}
                onChange={(e) => handleChange('frequency_days', e.target.value)}
                className="h-8 text-sm"
                data-testid="tool-frequency-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Last Calibration Date</Label>
              <Input
                type="date"
                value={form.calibration_date}
                onChange={(e) => handleChange('calibration_date', e.target.value)}
                className="h-8 text-sm"
                data-testid="tool-cal-date-input"
              />
            </div>
          </div>

          {/* Row 5: Location & Method */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Location</Label>
              <Input
                value={form.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="e.g. QC Lab, Shelf 3"
                className="h-8 text-sm"
                data-testid="tool-location-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Calibration Method</Label>
              <Select value={form.method || undefined} onValueChange={(val) => handleChange('method', val)}>
                <SelectTrigger className="h-8 text-sm" data-testid="tool-method-select">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  {methods.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 6: Acceptance Criteria & Standard */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Acceptance Criteria</Label>
              <Input
                value={form.acceptance_criteria}
                onChange={(e) => handleChange('acceptance_criteria', e.target.value)}
                placeholder="Tolerance / criteria"
                className="h-8 text-sm"
                data-testid="tool-criteria-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Standard Used for Criteria</Label>
              <Input
                value={form.standard_used_for_criteria}
                onChange={(e) => handleChange('standard_used_for_criteria', e.target.value)}
                placeholder="e.g. IS/ISO 3611"
                className="h-8 text-sm"
                data-testid="tool-standard-criteria-input"
              />
            </div>
          </div>

          {/* Row 7: Preferred Agency - linked to Supplier Management */}
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Preferred Calibration Agency</Label>
            <Select 
              value={form.preferred_calibration_agency || '__none__'} 
              onValueChange={(v) => handleChange('preferred_calibration_agency', v === '__none__' ? '' : v)}
            >
              <SelectTrigger className="h-8 text-sm" data-testid="tool-agency-select">
                <SelectValue placeholder="Select from approved suppliers..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {approvedSuppliers.map(supplier => (
                  <SelectItem key={supplier.id} value={supplier.supplier_name}>
                    {supplier.supplier_name} {supplier.supplier_code && `(${supplier.supplier_code})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {approvedSuppliers.length === 0 && (
              <p className="text-[10px] text-slate-400">No approved suppliers found. Add suppliers in Supplier Management.</p>
            )}
          </div>

          {/* Row 8: Remark */}
          <div className="space-y-1">
            <Label className="text-xs text-slate-600">Remark</Label>
            <Input
              value={form.remark}
              onChange={(e) => handleChange('remark', e.target.value)}
              placeholder="Any additional remarks"
              className="h-8 text-sm"
              data-testid="tool-remark-input"
            />
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="h-8 text-sm"
              data-testid="cancel-tool-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="h-8 text-sm bg-[#0F172A] hover:bg-[#1E293B] text-white"
              data-testid="save-tool-button"
            >
              {loading ? 'Saving...' : isEdit ? 'Update Instrument' : 'Create Instrument'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
